<script>
import { GlModal, GlFormGroup, GlFormInput, GlFormSelect, GlFormTextarea } from '@gitlab/ui';
import { isEqual } from 'lodash-es';
import { __, s__ } from '~/locale';
import { confirmAction } from '~/lib/utils/confirm_via_gl_modal/confirm_via_gl_modal';
import { isAbsolute, isValidURL } from '~/lib/utils/url_utility';

const emptyFields = () => ({
  title: '',
  resolvedBy: null,
  description: '',
  resolutionRationale: '',
  noteUrl: '',
});

// The ways a user can dismiss the modal.
const DISMISS_TRIGGERS = ['cancel', 'headerclose', 'esc', 'backdrop'];

// `isValidURL` still accepts a bare host such as `https://foo`, so the hostname has to end in a
// two-letter-or-longer TLD of its own.
const HOSTNAME_WITH_TLD = /\.[a-z]{2,}$/i;

export default {
  name: 'DecisionLogFormModal',
  components: {
    GlModal,
    GlFormGroup,
    GlFormInput,
    GlFormSelect,
    GlFormTextarea,
  },
  props: {
    visible: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The decision to edit. Leave it null to open the form empty for a new one.
     */
    decision: {
      type: Object,
      required: false,
      default: null,
    },
    participants: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  emits: ['hide', 'save'],
  data() {
    return {
      fields: emptyFields(),
      // What the form opened with, so a dismissal can tell an untouched form from an edited one.
      initialFields: emptyFields(),
      submitted: false,
    };
  },
  computed: {
    isEditing() {
      return Boolean(this.decision);
    },
    modalTitle() {
      return this.isEditing
        ? s__('WorkItemDecisionLog|Edit decision')
        : s__('WorkItemDecisionLog|New decision');
    },
    actionPrimary() {
      return {
        text: this.isEditing ? __('Save changes') : s__('WorkItemDecisionLog|Create decision'),
        attributes: { variant: 'confirm' },
      };
    },
    participantOptions() {
      return this.participants.map(({ id, name }) => ({ value: id, text: name }));
    },
    defaultResolvedBy() {
      return this.participants[0]?.id ?? null;
    },
    // A decision captured from a discussion already carries the place it was made, so the link is
    // a record rather than something to retype. A decision entered by hand has no source to keep.
    isNoteUrlReadOnly() {
      return Boolean(this.decision?.noteUrl);
    },
    noteUrlDescription() {
      return this.isNoteUrlReadOnly
        ? s__('WorkItemDecisionLog|Captured automatically from the source and cannot be changed.')
        : s__('WorkItemDecisionLog|Slack, Google Docs, GitLab, or anywhere the discussion lives.');
    },
    isTitleValid() {
      return this.fields.title.trim().length > 0;
    },
    isResolvedByValid() {
      return Boolean(this.fields.resolvedBy);
    },
    isDescriptionValid() {
      return this.fields.description.trim().length > 0;
    },
    // The source link is optional, so only a filled-in one has to hold up.
    isNoteUrlValid() {
      const noteUrl = this.fields.noteUrl.trim();

      if (!noteUrl) return true;
      if (!isAbsolute(noteUrl) || !isValidURL(noteUrl)) return false;

      return HOSTNAME_WITH_TLD.test(new URL(noteUrl).hostname);
    },
    isValid() {
      return (
        this.isTitleValid &&
        this.isResolvedByValid &&
        this.isDescriptionValid &&
        this.isNoteUrlValid
      );
    },
    isDirty() {
      return !isEqual(this.fields, this.initialFields);
    },
    discardConfirmMessage() {
      return this.isEditing
        ? s__('WorkItemDecisionLog|Are you sure you want to cancel editing this decision?')
        : s__('WorkItemDecisionLog|Are you sure you want to cancel creating this decision?');
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(visible) {
        if (visible) {
          this.resetForm();
        }
      },
    },
    decision() {
      this.resetForm();
    },
    // The deciders come from a query, so the default one can land after the form has opened. The
    // baseline moves with it, or an untouched form would read as edited.
    participants() {
      if (this.fields.resolvedBy) return;

      this.fields.resolvedBy = this.defaultResolvedBy;
      this.initialFields.resolvedBy = this.defaultResolvedBy;
    },
  },
  methods: {
    resetForm() {
      const { title, resolvedBy, description, resolutionRationale, noteUrl } =
        this.decision ?? emptyFields();

      this.submitted = false;
      this.fields = {
        title: title ?? '',
        resolvedBy: resolvedBy ?? this.defaultResolvedBy,
        description: description ?? '',
        resolutionRationale: resolutionRationale ?? '',
        noteUrl: noteUrl ?? '',
      };
      this.initialFields = { ...this.fields };
    },
    // Fields stay neutral until the first submit, so an untouched form does not open covered in
    // red. After that only the failures are marked.
    stateFor(isFieldValid) {
      return this.submitted && !isFieldValid ? false : null;
    },
    onPrimary(event) {
      // The modal closes itself on primary, which would hide the validation messages the user
      // needs to read.
      event.preventDefault();
      this.submitted = true;

      if (!this.isValid) return;

      // Closing is the parent's call, so it can keep the form up while a save is in flight.
      this.$emit('save', { ...this.fields });
    },
    // One handler for every way out, because `hide` covers Cancel, the X, Escape and the backdrop.
    async onHide(event) {
      if (!DISMISS_TRIGGERS.includes(event.trigger) || !this.isDirty) return;

      event.preventDefault();

      const confirmed = await confirmAction(this.discardConfirmMessage, {
        primaryBtnText: __('Discard changes'),
        primaryBtnVariant: 'danger',
        cancelBtnText: s__('WorkItemDecisionLog|Continue editing'),
      });

      if (confirmed) {
        this.$emit('hide');
      }
    },
  },
  actionCancel: { text: __('Cancel') },
  modalId: 'decision-log-form-modal',
  noteUrlPlaceholder: 'http://example.com',
};
</script>

<template>
  <gl-modal
    :modal-id="$options.modalId"
    :visible="visible"
    :title="modalTitle"
    :aria-label="modalTitle"
    :action-primary="actionPrimary"
    :action-cancel="$options.actionCancel"
    size="sm"
    @primary="onPrimary"
    @hide="onHide"
    @hidden="$emit('hide')"
  >
    <gl-form-group
      :label="s__('WorkItemDecisionLog|Decision (required)')"
      :state="stateFor(isTitleValid)"
      :invalid-feedback="s__('WorkItemDecisionLog|Enter a summary of the decision')"
      label-for="decision-log-summary"
    >
      <gl-form-input
        id="decision-log-summary"
        v-model="fields.title"
        :state="stateFor(isTitleValid)"
        data-testid="decision-summary-input"
      />
    </gl-form-group>

    <gl-form-group
      :label="s__('WorkItemDecisionLog|Decided by (required)')"
      :state="stateFor(isResolvedByValid)"
      :invalid-feedback="s__('WorkItemDecisionLog|Select who made this decision')"
      label-for="decision-log-decided-by"
    >
      <gl-form-select
        id="decision-log-decided-by"
        v-model="fields.resolvedBy"
        :options="participantOptions"
        :state="stateFor(isResolvedByValid)"
        data-testid="decision-decided-by-select"
      />
    </gl-form-group>

    <gl-form-group
      :label="s__('WorkItemDecisionLog|Context (required)')"
      :label-description="
        s__('WorkItemDecisionLog|Background on the decision and any other options considered.')
      "
      :state="stateFor(isDescriptionValid)"
      :invalid-feedback="s__('WorkItemDecisionLog|Describe the context for this decision')"
      label-for="decision-log-context"
    >
      <gl-form-textarea
        id="decision-log-context"
        v-model="fields.description"
        :state="stateFor(isDescriptionValid)"
        data-testid="decision-context-input"
      />
    </gl-form-group>

    <gl-form-group
      :label="s__('WorkItemDecisionLog|Why')"
      :label-description="
        s__('WorkItemDecisionLog|Rationale for choosing this option over others.')
      "
      label-for="decision-log-why"
    >
      <gl-form-textarea
        id="decision-log-why"
        v-model="fields.resolutionRationale"
        data-testid="decision-why-input"
      />
    </gl-form-group>

    <gl-form-group
      class="gl-mb-0"
      :label="s__('WorkItemDecisionLog|Source link')"
      :label-description="noteUrlDescription"
      :state="stateFor(isNoteUrlValid)"
      :invalid-feedback="
        s__('WorkItemDecisionLog|Enter a full link, starting with http:// or https://')
      "
      label-for="decision-log-source-link"
    >
      <gl-form-input
        id="decision-log-source-link"
        v-model="fields.noteUrl"
        type="url"
        :disabled="isNoteUrlReadOnly"
        :state="stateFor(isNoteUrlValid)"
        :placeholder="$options.noteUrlPlaceholder"
        data-testid="decision-source-link-input"
      />
    </gl-form-group>
  </gl-modal>
</template>
